import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, RotateCcw, X, CheckCircle2, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  useCreateQuizMutation,
  useGetMyQuizzesQuery,
  useUploadQuizCoverMutation,
} from '../../api/apiSlice';
import QuizEditor from '../../components/quiz/QuizEditor';
import CoverPicker from '../../components/quiz/CoverPicker';
import { useDraftAutosave, formatAgo } from '../../lib/useDraftAutosave';

const blankQuiz = () => ({
  title: '',
  description: '',
  questions: [
    {
      text: '',
      options: [{ text: '' }, { text: '' }],
      correctOptionIndex: 0,
      explanation: '',
    },
  ],
});

const QuizCreatePage = () => {
  const navigate = useNavigate();
  const [value, setValue] = useState(blankQuiz);
  const [pendingCover, setPendingCover] = useState(null); // File до создания
  const [createQuiz, { isLoading }] = useCreateQuizMutation();
  const [uploadCover] = useUploadQuizCoverMutation();
  const { data: my } = useGetMyQuizzesQuery();
  const overLimit = my && my.activeCount >= my.limit;

  // Автосейв в localStorage — не теряем ввод при переключении вкладок / refresh.
  // Сохраняем только если в форме что-то есть (title или хоть одно поле).
  const { restored, restore, dismissRestore, clear, savedAt } = useDraftAutosave(
    'quiz_create',
    value,
    {
      shouldSave: (v) =>
        Boolean(v?.title?.trim() || v?.description?.trim() || v?.questions?.some((q) => q.text?.trim() || q.options?.some((o) => o.text?.trim()))),
    }
  );

  const submit = async () => {
    try {
      const res = await createQuiz(value).unwrap();
      const quizId = res.quiz._id;
      // Успешно создали → стираем черновик чтобы не предлагал восстановить.
      clear();

      // Если пользователь приложил обложку до сохранения — заливаем сразу
      // после создания. Не блокируем переход на edit ошибкой обложки.
      if (pendingCover) {
        try {
          await uploadCover({ id: quizId, coverFile: pendingCover }).unwrap();
        } catch (err) {
          toast.error(err?.data?.message || 'Квиз создан, но обложка не загрузилась');
        }
      }

      // После создания возвращаем на ЛИСТ квизов — юзер видит, что квиз
      // действительно появился. Раньше шли сразу на edit того же квиза —
      // пользователю казалось «ничего не произошло, я всё ещё в редакторе».
      toast.success('Квиз создан');
      navigate('/dashboard/student/quizzes', {
        state: { justCreatedId: quizId },
      });
    } catch (err) {
      const code = err?.data?.code;
      if (code === 'QUIZ_LIMIT_REACHED') {
        toast.error(err?.data?.message);
      } else {
        toast.error(err?.data?.message || 'Не удалось создать');
      }
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <Link
        to="/dashboard/student/quizzes"
        className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> К моим квизам
      </Link>

      <div className="flex items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
          Новый квиз
        </h1>
        {/* Тонкий индикатор «сохранено N сек/мин назад» — показывает что
            автосейв работает, юзер спокоен. */}
        {savedAt && (
          <span className="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
            Сохранено {formatAgo(savedAt)}
          </span>
        )}
      </div>

      {/* Banner — есть черновик. Показываем ОДИН раз при mount если найден.
          Юзер решает: восстановить или начать с нуля. */}
      {restored && (
        <div className="mb-5 rounded-2xl border border-indigo-200 dark:border-indigo-900/40 bg-indigo-50/50 dark:bg-indigo-950/20 p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-100 dark:bg-indigo-500/15 text-indigo-600 dark:text-indigo-400">
              <Save className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-gray-900 dark:text-white">
                Найден черновик
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">
                Вы что-то набирали ранее ({formatAgo(restored.savedAt)}). Восстановить?
              </p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                type="button"
                onClick={() => {
                  setValue(restored.value);
                  restore();
                }}
                className="inline-flex items-center gap-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 text-xs font-semibold transition"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Восстановить
              </button>
              <button
                type="button"
                onClick={dismissRestore}
                className="inline-flex items-center gap-1 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 px-2 py-1.5 text-xs font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition"
                title="Удалить черновик"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-6">
        <CoverPicker
          mode="staged"
          pendingFile={pendingCover}
          onPick={setPendingCover}
          onRemovePending={() => setPendingCover(null)}
          helper="Загрузится сразу после создания квиза. Можно пропустить и добавить позже."
        />

        <QuizEditor
          value={value}
          onChange={setValue}
          onSubmit={submit}
          isSubmitting={isLoading}
          submitLabel="Создать квиз"
          banner={
            overLimit && (
              <div className="rounded-2xl border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-950/20 p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800 dark:text-amber-200">
                  У вас уже {my.limit} активных квизов. Сохранение будет отклонено сервером —
                  удалите старый, чтобы создать новый.
                </div>
              </div>
            )
          }
        />
      </div>
    </div>
  );
};

export default QuizCreatePage;
